/**
 * dateWindow.js — shared temporal filters for list pages.
 *
 * Gives every list page a uniform "Date" dropdown so ops staff can scope
 * Orders / Transfers / Returns / Receiving to the same date buckets without
 * each page reinventing the logic.
 *
 * Buckets (keyed by string so they can live in a <select>):
 *   ''           → no filter (all time)
 *   'today'      → created_at within the current calendar day
 *   'this_week'  → rolling last 7 days (inclusive)
 *   'last_7'     → alias preserved for legacy callers
 *   'last_30'    → rolling last 30 days (inclusive)
 *
 * ETA variants (used on inbound pages like Receiving) operate on an
 * "arrival" date (YYYY-MM-DD or full ISO) rather than created_at:
 *   'arriving_today'   → ETA is today
 *   'arriving_week'    → ETA within the next 7 days (today + 7)
 *   'arriving_next_week' → ETA in days 8..14
 *   'overdue'          → ETA is in the past and row is not completed
 */

const MS_DAY = 24 * 60 * 60 * 1000;

function _parse(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function _startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Return true if the row's `created_at` matches the selected window. */
export function matchesCreatedWindow(createdAt, windowKey) {
  if (!windowKey) return true;
  const d = _parse(createdAt);
  if (!d) return false;
  const now = new Date();
  const today = _startOfDay(now);
  if (windowKey === 'today') {
    return d >= today && d < new Date(today.getTime() + MS_DAY);
  }
  if (windowKey === 'this_week' || windowKey === 'last_7') {
    return (now.getTime() - d.getTime()) <= 7 * MS_DAY && d <= now;
  }
  if (windowKey === 'last_30') {
    return (now.getTime() - d.getTime()) <= 30 * MS_DAY && d <= now;
  }
  return true;
}

/**
 * Return true if the row's `eta` (expected arrival) matches the selected
 * window. `isCompleted` opts the row out of the `overdue` bucket once it has
 * been received.
 */
export function matchesEtaWindow(eta, windowKey, { isCompleted = false } = {}) {
  if (!windowKey) return true;
  const d = _parse(eta);
  if (!d) return false;
  const now = new Date();
  const today = _startOfDay(now);
  const tomorrow = new Date(today.getTime() + MS_DAY);
  if (windowKey === 'arriving_today') {
    return d >= today && d < tomorrow;
  }
  if (windowKey === 'arriving_week') {
    return d >= today && d < new Date(today.getTime() + 7 * MS_DAY);
  }
  if (windowKey === 'arriving_next_week') {
    const start = new Date(today.getTime() + 7 * MS_DAY);
    const end = new Date(today.getTime() + 14 * MS_DAY);
    return d >= start && d < end;
  }
  if (windowKey === 'overdue') {
    return d < today && !isCompleted;
  }
  return true;
}

/** Standard option list for created_at windows — drop straight into FilterBar. */
export const CREATED_WINDOW_OPTIONS = [
  { value: '', label: 'Any date' },
  { value: 'today', label: 'Created today' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_30', label: 'Last 30 days' },
];

/** Standard option list for ETA windows on inbound pages. */
export const ETA_WINDOW_OPTIONS = [
  { value: '', label: 'Any ETA' },
  { value: 'arriving_today', label: 'Arriving today' },
  { value: 'arriving_week', label: 'Arriving this week' },
  { value: 'arriving_next_week', label: 'Arriving next week' },
  { value: 'overdue', label: 'Overdue' },
];

/**
 * Return { label, cls } for a human-friendly ETA chip, or null when the row
 * has no ETA. Kept beside the filter matcher so chip colours never drift from
 * the selector buckets.
 */
export function getEtaChip(eta, { isCompleted = false } = {}) {
  const d = _parse(eta);
  if (!d) return null;
  const now = new Date();
  const today = _startOfDay(now);
  const tomorrow = new Date(today.getTime() + MS_DAY);
  const weekOut = new Date(today.getTime() + 7 * MS_DAY);
  if (!isCompleted && d < today) {
    return { label: `Overdue · ${d.toLocaleDateString()}`, cls: 'bg-rose-100 text-rose-800' };
  }
  if (d < tomorrow) {
    return { label: 'Arriving today', cls: 'bg-emerald-100 text-emerald-800' };
  }
  if (d < weekOut) {
    return {
      label: `Arriving ${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`,
      cls: 'bg-sky-100 text-sky-800',
    };
  }
  return { label: `ETA ${d.toLocaleDateString()}`, cls: 'bg-muted text-muted-foreground' };
}
